import { c as _c } from "react/compiler-runtime";
import { DateCell } from "@components/common/date-cell";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { Text } from "@flat-ui";
import { useMembers } from "@hooks/use-members";
import { Box, Stack } from "@mui-wrapper";
import { getMarkupCreator } from "@pages/project-details/project-markups/markup-details-utils";
import { getMarkupActivity } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel-activity-utils";
import { MarkupSidePanelSection } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel-section";
import { withEllipsis } from "@styles/common-styles";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { getPrettyName } from "@utils/user-utils";

/** Who did something, with their avatar, and when */
function ActivityUserAndDate(t0) {
  const $ = _c(19);
  const {
    userId,
    date
  } = t0;
  const {
    companyMembers,
    projectMembers
  } = useMembers();
  const currentUser = useAppSelector(currentUserSelector);
  let t1;
  if ($[0] !== companyMembers || $[1] !== currentUser || $[2] !== projectMembers || $[3] !== userId) {
    t1 = getMarkupCreator({
      creatorId: userId,
      companyMembers,
      projectMembers
    }) ?? (userId === currentUser?.identity ? currentUser : undefined);
    $[0] = companyMembers;
    $[1] = currentUser;
    $[2] = projectMembers;
    $[3] = userId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const member = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: 1,
      minWidth: 0
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== member) {
    t3 = member && <UserAvatarWithMenu member={member} size="x-small" />;
    $[6] = member;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== member) {
    t4 = getPrettyName(member) || "Unknown user";
    $[8] = member;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <Text variant="bodyS" component="var" sx={withEllipsis}>{t4}</Text>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      color: "text.secondary",
      ml: "auto",
      flexShrink: 0
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== date) {
    t7 = <Text variant="bodyS" component="div" sx={t6}><DateCell date={date} shouldShowTimeInTooltip={true} /></Text>;
    $[13] = date;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t5 || $[17] !== t7) {
    t8 = <Stack direction="row" sx={t2}>{t3}{t5}{t7}</Stack>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

/** A change of the annotation: what, who and when */
function ActivityRow(t0) {
  const $ = _c(10);
  const {
    label,
    date,
    userId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: "grid",
      gridTemplateColumns: "6.5rem minmax(0, 1fr)",
      columnGap: 1,
      alignItems: "center"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      color: "text.secondary"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <Text variant="heading12" sx={t2}>{label}</Text>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== date || $[5] !== userId) {
    t4 = <ActivityUserAndDate userId={userId} date={date} />;
    $[4] = date;
    $[5] = userId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <Box sx={t1}>{t3}{t4}</Box>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}

/** What happened to the annotation and by whom, from its creation on, the oldest first */
export function MarkupSidePanelActivity(t0) {
  const $ = _c(19);
  const {
    markup
  } = t0;
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== markup) {
    const activity = getMarkupActivity(markup);
    T1 = MarkupSidePanelSection;
    t3 = <>Activity (<var>{activity.length}</var>)</>;
    t4 = "activity";
    t5 = "markup-side-panel-activity";
    T0 = Stack;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        gap: 1.5
      };
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    t2 = activity.map(_temp);
    $[0] = markup;
    $[1] = T0;
    $[2] = T1;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    T0 = $[1];
    T1 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[9] !== T0 || $[10] !== t1 || $[11] !== t2) {
    t6 = <T0 sx={t1}>{t2}</T0>;
    $[9] = T0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== T1 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <T1 title={t3} trackingName={t4} dataTestId={t5}>{t6}</T1>;
    $[13] = T1;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function _temp(entry) {
  return <ActivityRow key={entry.label} {...entry} />;
}